/**
 * axios encapsulation
 */
import qs from "qs";
import axios, { Axios, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import {
  URLInterface,
  RequestMethodProps,
  AxiosProps,
  RequestConfigProps,
  HandleRequestResponse,
  DomainAryProps,
  ServiceConfigProps,
  ResponseTypes,
  AxiosTypes,
} from "./index.inter";

/** Response error handling logic */
const onRejected = (error: any) => {
  const { message, response } = error;
  const err = { code: response ? response.status : 0, message };
  if (response) {
    const { status, data }: AxiosResponse = response;
    switch (status) {
      case 400:
        err.message = `${status} Request parameter error`;
        break;
      case 401:
        err.message = `${status} Current request requires user verification`;
        break;
      case 403:
        err.message = `${status} Server understood the request but refuses to authorize it`;
        break;
      case 404:
        err.message = `${status} Request path does not exist`;
        break;
      case 405:
        err.message = `${status} The request method is not allowed for the requested resource`;
        break;
      case 500:
        err.message = `${status} Server encountered an unexpected condition`;
        break;
      case 502:
        err.message = `${status} Gateway error`;
        break;
      case 503:
        err.message = `${status} Server is currently unable to handle the request due to maintenance or overload`;
        break;
      case 504:
        err.message = `${status} Response timeout`;
        break;
      case 505:
        err.message = `${status} HTTP version not supported`;
        break;
      default:
        err.message = `${status} ${data?.message || message}`;
        break;
    }
  }
  if (message.includes("timeout")) {
    err.message = "Network request timeout";
    err.code = 504;
  }
  if (message.includes("Network")) {
    err.message = window.navigator.onLine ? "Network not connected" : "Network error";
    err.code = -7;
  }
  if (axios.isCancel(error)) {
    // Cancelled duplicate request
    return Promise.reject({ code: "000" });
  } else {
    return Promise.reject({ code: "ERROR_NETWORK" });
  }
};

/**
 * Returns base domain and request headers based on request path
 * @param {URLInterface}       url    Request interface
 * @param {RequestConfigProps} config Request configuration
 * @returns {HandleRequestResponse}
 */
const handleRequest = (
  url: URLInterface,
  config?: RequestConfigProps
): HandleRequestResponse => {
  if (config) {
    const { domainAry, domainName, headers }: RequestConfigProps = config;
    // Request domain
    let domain: string | undefined;
    let header: any;
    let timeout: number | undefined;
    if (domainAry) {
      for (let index = 0; index < domainAry.length; index++) {
        const dm: DomainAryProps = domainAry[index];
        if (!dm.serviceName) {
          continue;
        } else {
          const service: string | ServiceConfigProps | undefined =
            dm.serviceName.find((sn: string | ServiceConfigProps) => {
              return typeof sn === "string"
                ? url.path.includes(sn)
                : url.path.includes(sn.name);
            });
          if (service) {
            if (typeof service === "string") {
              header = dm.headers;
              domain = dm.domainName;
              timeout = dm.timeout;
            } else {
              header = service.header || dm.headers;
              domain = dm.domainName;
              timeout = service.timeout || dm.timeout;
            }
            break;
          }
        }
      }
    }
    return {
      baseURL: domain || domainName,
      header: header || headers,
      timeout,
    };
  }
  return {};
};

/** Initialize project */
export class BaseAxios {
  constructor(config?: AxiosProps) {
    this.closeCancelRequest = config?.closeCancelRequest; // Default is not to close automatic cancellation of duplicate requests
    this.requestConfig = config?.requestConfig; // Request configuration
    this.timeout = config?.timeout; // Request timeout
    this.config = config;
    this.instance = axios.create();
    // Register request and response interceptors
    this.initInterceptors();
  }

  /** Request configuration */
  private config?: AxiosProps;

  /** Request configuration */
  private requestConfig?: RequestConfigProps;

  /** Request timeout */
  private timeout?: number;

  /** Whether to close cancellation of duplicates */
  private closeCancelRequest?: boolean;

  /** New request instance */
  private instance: Axios;

  /** Initialization: register interceptors */
  private initInterceptors() {
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        return config;
      }
    );
    this.instance.interceptors.response.use(
      (response: any) => {
        return response;
      },
      (error) => {
        return onRejected(error);
      }
    );
  }

  /**
   * Send request
   * @param {URLInterface} url Interface
   * @param inParams
   * @param {RequestMethodProps} config Other configurations
   * @returns {Promise<any>}
   */
  request = <T>(
    url: any,
    inParams: any = {
      // placeholderLang: ""
    },
    config: RequestMethodProps = {}
  ): Promise<T> => {
    url = url as URLInterface;
    const { path, type = "get", baseURL: methodBaseURL } = url;
    const { onDynamicHeader, onHandleResponseData }: AxiosProps =
      this.config || {};
    const {
      headers = {},
      timeout: methodTimeout,
      mergeHeader = true,
      ...methodConfig
    } = config;
    const {
      header = {},
      baseURL,
      timeout,
    }: HandleRequestResponse = handleRequest(url, this.requestConfig);
    const dynamicHeader = onDynamicHeader ? onDynamicHeader() : {};
    const baseConfig = {
      baseURL: methodBaseURL || baseURL,
      headers: mergeHeader
        ? { ...dynamicHeader, ...header, ...headers }
        : { ...dynamicHeader, ...(headers || header) },
      timeout: methodTimeout || timeout || this.timeout,
    };
    if (/(post|POST)/.test(type) && this.config.handleSign) {
      inParams = this.config.handleSign(inParams);
    }
    // Send request
    const instanceResponse: Promise<AxiosTypes<ResponseTypes<T>>> =
      this.instance.request({
        url: path,
        method: type,
        data: /(post|POST)/.test(type) ? inParams : undefined, // post request method
        params: /(get|GET)/.test(type) ? inParams : undefined, // get request method
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: "repeat" }),
        ...baseConfig,
        ...methodConfig,
      });
    if (onHandleResponseData) {
      return new Promise((resolve, reject) => {
        instanceResponse
          .then((response) => {
            response.requestData = inParams;
            onHandleResponseData(response, config, this)
              .then((res: any) => {
                resolve(res);
              })
              .catch((err) => {
                reject(err);
              });
          })
          .catch((err) => {
            reject(err);
          });
      });
    } else {
      return new Promise<T>((resolve, reject) => {
        instanceResponse
          .then((res) => {
            const data = res.data;
            resolve(data.data);
          })
          .catch((error) => {
            //let e = JSON.stringify(error);
            reject({ code: "ERROR_NETWORK" });
          });
      });
    }
  };
}
